/** A centre's own fields for the people it holds: a student number, a file reference. */

export type CustomFieldType = "text" | "number" | "date" | "select";

export const CUSTOM_FIELD_TYPES: CustomFieldType[] = ["text", "number", "date", "select"];

/** How many fields one centre may define. The server holds the same limit. */
export const MAX_CUSTOM_FIELDS = 20;

/** The longest label and value the server accepts. */
export const CUSTOM_FIELD_LABEL_MAX = 80;
export const CUSTOM_FIELD_VALUE_MAX = 200;

export interface PatientCustomField {
    id: string;
    center_id: string;
    label: string;
    field_type: CustomFieldType;
    /** A select's choices, in order; empty for any other type. */
    options: string[];
    required: boolean;
    /** No two people of the centre may share a value — for identifiers. */
    unique: boolean;
    position: number;
}

/** A new field. Its type is chosen here and never changes afterwards. */
export interface PatientCustomFieldInput {
    label: string;
    field_type: CustomFieldType;
    options?: string[];
    required?: boolean;
    unique?: boolean;
}

/** What may change of a field once it exists: everything but its type. */
export type PatientCustomFieldUpdate = Partial<Omit<PatientCustomFieldInput, "field_type">>;

/**
 * One person's values, with the fields they answer. Values are text as the
 * server stores them: numbers as a decimal string, dates as `YYYY-MM-DD`.
 * A field with no value is absent from `values`.
 */
export interface PatientCustomValues {
    fields: PatientCustomField[];
    values: Record<string, string>;
}

/** A partial save: only the ids present change; `null` or `""` clears one. */
export type PatientCustomValuesInput = Record<string, string | null>;

/** The ids of the fields in order, one moved `by` places; unchanged when it cannot move. */
export function moveField(fields: PatientCustomField[], index: number, by: -1 | 1): string[] {
    const ids = fields.map((field) => field.id);
    const target = index + by;
    if (target < 0 || target >= ids.length) return ids;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    return ids;
}

/** Required fields that have no value yet. */
export function missingRequired(data: PatientCustomValues): PatientCustomField[] {
    return data.fields.filter((field) => field.required && !data.values[field.id]?.trim());
}

/**
 * What a form can tell about one value before asking the server: an i18n key,
 * or null when it may be sent. The server checks again, and alone knows
 * whether a unique value is taken.
 */
export function validateCustomValue(field: PatientCustomField, value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) return field.required ? "center.patientFields.errors.requiredMissing" : null;
    if (trimmed.length > CUSTOM_FIELD_VALUE_MAX) return "center.patientFields.errors.valueTooLong";
    if (field.field_type === "number" && !Number.isFinite(Number(trimmed))) {
        return "center.patientFields.errors.valueInvalid";
    }
    return null;
}
